import { GridApi, RowPinnedType } from "ag-grid-community";

/**
 * Loading-overlay helpers for AG Grid v32+.
 *
 * `api.showLoadingOverlay()` is deprecated since v32 in favour of the `loading`
 * grid option. The option is sticky (the overlay stays until `loading` is set
 * back to false), whereas the v31 API overlay was also dismissed by the grid
 * itself on the next row-data update. These helpers drive the `loading` option
 * but keep that v31 behaviour, so screens written against it don't end up with
 * a spinner that never goes away.
 *
 * The v32+ loading overlay is also "exclusive": while it shows, the grid
 * suppresses cell focus and moves browser focus into the overlay, and on hide it
 * only returns focus to the DOM element that had it. Screens that redraw or
 * replace their rows while loading (plan detail after a cell edit) lose that
 * element, so the focused cell was lost (cell ranges survive). The helpers
 * remember the focused cell when the overlay shows and restore it by position
 * when it hides, as the v31 (non-exclusive) overlay left focus untouched.
 */
const pendingRowDataListeners = new WeakSet<GridApi>();

interface SavedCellPosition {
  rowIndex: number;
  colId: string;
  rowPinned: RowPinnedType;
}

interface SavedFocus {
  focusedCell: SavedCellPosition;
  /** Whether keyboard focus was inside the grid (the overlay takes it away). */
  gridHadFocus: boolean;
}

const savedFocus = new WeakMap<GridApi, SavedFocus>();

/** Whether browser focus is inside a grid (or nowhere, as after the overlay drops it). */
function focusInGridOrNowhere(): boolean {
  const active = document.activeElement as HTMLElement | null;
  return !active || active === document.body || !!active.closest?.(".ag-root-wrapper");
}

function focusInGrid(): boolean {
  const active = document.activeElement as HTMLElement | null;
  return !!active && active !== document.body && !!active.closest?.(".ag-root-wrapper");
}

function saveFocus(api: GridApi): void {
  if (savedFocus.has(api)) return; // keep the state from before the first show
  const focused = api.getFocusedCell?.();
  if (!focused) return;
  savedFocus.set(api, {
    focusedCell: { rowIndex: focused.rowIndex, colId: focused.column.getColId(), rowPinned: focused.rowPinned },
    gridHadFocus: focusInGrid(),
  });
}

function restoreFocus(api: GridApi): void {
  const saved = savedFocus.get(api);
  savedFocus.delete(api);
  if (!saved) return;
  // After the current task, so a row-data swap applied by change detection in
  // the same turn (e.g. `this.rowData = [...]` before hiding) has settled.
  setTimeout(() => {
    if (api.isDestroyed?.() || api.getGridOption("loading")) return;
    const { focusedCell, gridHadFocus } = saved;
    // setFocusedCell also moves browser focus, so only when the overlay took
    // focus from the grid and nothing outside the grid has been focused since.
    if (!gridHadFocus || !focusInGridOrNowhere() || !api.getColumn?.(focusedCell.colId)) return;
    if (focusedCell.rowPinned || focusedCell.rowIndex < api.getDisplayedRowCount()) {
      api.setFocusedCell(focusedCell.rowIndex, focusedCell.colId, focusedCell.rowPinned);
    }
  });
}

/** Replacement for the deprecated `api.showLoadingOverlay()`. */
export function showGridLoadingOverlay(api: GridApi | null | undefined): void {
  if (!api || api.isDestroyed?.()) return;
  saveFocus(api);
  api.setGridOption("loading", true);
  if (pendingRowDataListeners.has(api)) return;
  pendingRowDataListeners.add(api);
  const onRowDataUpdated = () => {
    pendingRowDataListeners.delete(api);
    if (api.isDestroyed?.()) return;
    api.removeEventListener("rowDataUpdated", onRowDataUpdated);
    api.setGridOption("loading", false);
    restoreFocus(api);
  };
  api.addEventListener("rowDataUpdated", onRowDataUpdated);
}

/**
 * Replacement for `api.hideOverlay()` on grids that use
 * {@link showGridLoadingOverlay}: in v32+ `hideOverlay()` does not hide the
 * overlay while `loading` is true, so clear the flag first.
 */
export function hideGridOverlay(api: GridApi | null | undefined): void {
  if (!api || api.isDestroyed?.()) return;
  api.setGridOption("loading", false);
  api.hideOverlay();
  restoreFocus(api);
}
